import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMe } from '@/lib/me';
import { useApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { allTimeZones, browserTimeZone } from '@/lib/dates';
import { NameForm } from '@/components/name-form';
import { Card, ErrorText, Field, Select } from '@/components/ui';

/** Who you are: sign-in email, the name on certificates, and your time zone. */
export function AccountProfilePage() {
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <Card title="Signed in as">
        <p className="text-sm">{user?.email}</p>
      </Card>
      <DisplayName />
      <TimeZone />
    </div>
  );
}

/** The name printed on certificates, with the change rules. */
function DisplayName() {
  const me = useMe();
  if (!me.data) return null;
  return (
    <Card title="Name on certificates">
      <NameForm me={me.data} />
    </Card>
  );
}

/** The zone used for certificate issue dates when a submission doesn't send one. */
function TimeZone() {
  const api = useApi();
  const me = useMe();
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: api.setTimeZone,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
  if (!me.data) return null;
  const detected = browserTimeZone();
  const current = me.data.timeZone ?? detected;
  const zones = allTimeZones();
  const options = zones.includes(current) ? zones : [current, ...zones];
  return (
    <Card title="Time zone">
      <Field
        label="Your time zone"
        htmlFor="time-zone"
        hint="Certificates show their issue date in your time zone when you earn them."
      >
        <Select
          id="time-zone"
          value={current}
          disabled={save.isPending}
          onChange={(e) => save.mutate(e.target.value)}
        >
          {options.map((z) => (
            <option key={z} value={z}>
              {z.replace(/_/g, ' ')}
            </option>
          ))}
        </Select>
      </Field>
      {current !== detected && (
        <p className="mt-3 text-sm text-muted-foreground">
          This device is set to {detected.replace(/_/g, ' ')}.{' '}
          <button
            type="button"
            className="text-primary hover:underline"
            disabled={save.isPending}
            onClick={() => save.mutate(detected)}
          >
            Use it instead
          </button>
        </p>
      )}
      <ErrorText error={save.error} />
    </Card>
  );
}
